/* eslint-disable @typescript-eslint/no-explicit-any */
import { splitEncoding } from '@contentful/live-preview';

import { StateConstraint } from '@/contentful/types';
import { userLocationType } from '@/hooks/geo';

function sanitizeList(list?: string[]): string[] | undefined {
  return list && list.map((value) => splitEncoding(value).cleaned);
}

// userLocation: is the user state or zip code, like CA, HI, WA, 90210, 10020...
function determineDataMatches(
  list: string[],
  userLocation: string,
  constraintType: boolean
): boolean {
  const matches = list.includes(userLocation);
  return constraintType ? matches : !matches;
}

// Checks if the state constraint applies to the user location
// stateConstraint: is the constraint state to be checked
// userLocation: is the user state and zip code, like CA, HI, WA, etc.
export function checkStateConstraint({
  stateConstraint,
  userLocation,
}: {
  stateConstraint: StateConstraint;
  userLocation: userLocationType;
}): boolean {
  // get the user state
  const userState = userLocation.state;

  // get the user zip code
  const userZipCode =
    userLocation.zipCode &&
    userLocation.zipCode.length >= 5 &&
    userLocation.zipCode.substring(0, 5);

  if (!userState && !userZipCode) {
    return false;
  }

  // get the constraint zip code list
  const constraintZipCode = sanitizeList(
    stateConstraint?.fields?.zipCodeList?.split(',')
  );

  // get the constraint state list
  const constraintState = sanitizeList(stateConstraint?.fields?.states);

  // get the zip code list if exists
  const zipCodeList =
    constraintZipCode && constraintZipCode.length > 0 && constraintZipCode;

  // get the state list if exists
  const stateList =
    constraintState && constraintState.length > 0 && constraintState;

  // get the constraint type
  const constraintType = stateConstraint?.fields?.constraintType;

  // if the user zip code and the list of zip codes is set
  // and the list of states for the constraint is not set
  // then the zip code is used
  if (userZipCode && zipCodeList && !stateList) {
    return determineDataMatches(zipCodeList, userZipCode, constraintType);
  }

  // if the user state and the list of states is set
  // then the state is used
  if (userState && stateList) {
    return determineDataMatches(stateList, userState, constraintType);
  }

  // if none applies, return false
  return false;
}

// Checks which state contraint applies from the list of constraints and return the correct info
// defaultValue: is the default object to be returned in case it applies
// constraints (optional): is the list of state constraints to be checked
// userLocation: contains user state, country and zip code..
export function getStateConstraintToApply({
  defaultValue,
  constraints,
  userLocation,
}: {
  defaultValue?: any;
  constraints?: StateConstraint[];
  userLocation: userLocationType | null;
}): any {
  // check if the constraint or the user location is undefined, if yes, return the default value
  if (
    !constraints ||
    !userLocation ||
    (!userLocation.state && !userLocation.zipCode)
  ) {
    return defaultValue;
  }

  // filter the constraints applied to this content
  const constraintFound = constraints.find(
    (stateConstraint: StateConstraint) => {
      return checkStateConstraint({ stateConstraint, userLocation });
    }
  );

  // return the default value or alternate Content if it exists
  return constraintFound?.fields.alternateContent || defaultValue;
}
